import type { SettingsNavItem } from '../components/app-shell/AppShell';
import {
  IconBrain,
  IconClipboardCheck,
  IconLayoutDashboard,
  IconPresentation,
  IconReceiptDollar,
  IconReportAnalytics,
  IconSettings,
  IconUsers,
  IconWallet,
} from '@tabler/icons-react';

export type ShellPersona = 'parent' | 'staff' | 'administrator' | 'super-administrator' | 'guest';

export type ModuleId =
  | 'assistant'
  | 'run-reports'
  | 'admissions'
  | 'attendance'
  | 'control-plane'
  | 'financial-aid'
  | 'payroll'
  | 'tuition'
  | 'students-families'
  | 'faculty'
  | 'settings';

export type ShellModule = {
  id: ModuleId;
  label: string;
  description: string;
  badge: string;
  icon: SettingsNavItem['icon'];
  routeRoot: string;
  entryPaths: Partial<Record<ShellPersona, string>>;
  showOnLanding: boolean;
  allowedPersonas: ShellPersona[];
  retired?: boolean;
};

const STAFF_PERSONAS: ShellPersona[] = ['staff', 'administrator', 'super-administrator'];
const ADMIN_PERSONAS: ShellPersona[] = ['administrator', 'super-administrator'];

export const SHELL_MODULES: ShellModule[] = [
  {
    id: 'assistant',
    label: 'Assistant',
    description: 'Open the full-page assistant workspace and package-owned assistant home/workspace surfaces.',
    badge: 'AI',
    icon: IconBrain,
    routeRoot: '/school/assistant',
    entryPaths: {
      staff: '/school/assistant',
      administrator: '/school/assistant',
      'super-administrator': '/school/assistant',
    },
    showOnLanding: true,
    allowedPersonas: STAFF_PERSONAS,
  },
  {
    id: 'run-reports',
    label: 'Run Reports',
    description: 'Build, save, schedule, and export cross-module staff reports.',
    badge: 'Reports',
    icon: IconReportAnalytics,
    routeRoot: '/school/run-reports',
    entryPaths: {
      staff: '/school/run-reports',
      administrator: '/school/run-reports',
      'super-administrator': '/school/run-reports',
    },
    showOnLanding: true,
    allowedPersonas: STAFF_PERSONAS,
  },
  {
    id: 'admissions',
    label: 'Admissions',
    description: 'Review applications, decisions, and enrollment progress.',
    badge: 'Pipeline',
    icon: IconLayoutDashboard,
    routeRoot: '/school/admissions',
    entryPaths: {
      staff: '/school/admissions/dashboard',
      administrator: '/school/admissions/dashboard',
      'super-administrator': '/school/admissions/dashboard',
    },
    showOnLanding: true,
    allowedPersonas: STAFF_PERSONAS,
  },
  {
    id: 'attendance',
    label: 'Attendance',
    description: 'Monitor attendance health, record paper-sheet attendance, preview teacher entry, and export CSV evidence.',
    badge: 'Records',
    icon: IconClipboardCheck,
    routeRoot: '/school/attendance',
    entryPaths: {
      staff: '/school/attendance',
      administrator: '/school/attendance',
      'super-administrator': '/school/attendance',
    },
    showOnLanding: true,
    allowedPersonas: STAFF_PERSONAS,
  },
  {
    id: 'financial-aid',
    label: 'Financial Aid',
    description: 'Process awards, reviews, and family scholarship requests.',
    badge: 'Awards',
    icon: IconWallet,
    routeRoot: '/school/financialaid',
    entryPaths: {
      staff: '/school/financialaid/dashboard',
      administrator: '/school/financialaid/dashboard',
      'super-administrator': '/school/financialaid/dashboard',
    },
    showOnLanding: true,
    allowedPersonas: STAFF_PERSONAS,
  },
  {
    id: 'payroll',
    label: 'Payroll',
    description: 'Prepare pay periods, review employee compensation, and govern payroll exports.',
    badge: 'Payroll',
    icon: IconWallet,
    routeRoot: '/school/payroll',
    entryPaths: {
      administrator: '/school/payroll',
      'super-administrator': '/school/payroll',
    },
    showOnLanding: true,
    allowedPersonas: ADMIN_PERSONAS,
    retired: true,
  },
  {
    id: 'tuition',
    label: 'Tuition',
    description: 'Review family balances, collection health, and billing oversight.',
    badge: 'Billing',
    icon: IconReceiptDollar,
    routeRoot: '/school/tuition',
    entryPaths: {
      staff: '/school/tuition',
      administrator: '/school/tuition',
      'super-administrator': '/school/tuition',
    },
    showOnLanding: true,
    allowedPersonas: STAFF_PERSONAS,
  },
  {
    id: 'students-families',
    label: 'Students & Families',
    description: 'Manage family records, student lifecycle, communications, and tuition context.',
    badge: 'CRM',
    icon: IconUsers,
    routeRoot: '/school/students',
    entryPaths: {
      staff: '/school/students/dashboard',
      administrator: '/school/students/dashboard',
      'super-administrator': '/school/students/dashboard',
    },
    showOnLanding: true,
    allowedPersonas: STAFF_PERSONAS,
  },
  {
    id: 'control-plane',
    label: 'Control Plane',
    description: 'Super-administrator operator tooling for payments, bridge diagnostics, workflows, and UI consultation.',
    badge: 'Ops',
    icon: IconSettings,
    routeRoot: '/super-administrator/control-plane',
    entryPaths: {
      'super-administrator': '/super-administrator/control-plane/payments',
    },
    showOnLanding: true,
    allowedPersonas: ['super-administrator'] as ShellPersona[],
  },
  {
    id: 'faculty',
    label: 'Faculty',
    description: 'Manage faculty directory, profiles, assignments, and communications.',
    badge: 'Staff',
    icon: IconPresentation,
    routeRoot: '/school/faculty',
    entryPaths: {
      staff: '/school/faculty',
      administrator: '/school/faculty',
      'super-administrator': '/school/faculty',
    },
    showOnLanding: true,
    allowedPersonas: STAFF_PERSONAS,
  },
];

export const SETTINGS_MODULE: ShellModule = {
  id: 'settings',
  label: 'Settings',
  description: 'Configure organization, access, and policy controls.',
  badge: 'Admin',
  icon: IconSettings,
  routeRoot: '/school/settings',
  entryPaths: {
    administrator: '/school/settings/general',
    'super-administrator': '/school/settings/general',
  },
  showOnLanding: false,
  // Administration bundle — restricted to administrator and super-administrator personas.
  // Staff (non-admin) must not see settings in the sidebar or be able to navigate to it.
  // See: apps/ystnj/documentation/prd/settings/admin-settings/v1.0/prd.md
  allowedPersonas: ['administrator', 'super-administrator'] as ShellPersona[],
};

export const buildNavItems = (persona: ShellPersona): SettingsNavItem[] =>
  SHELL_MODULES.filter((module) => !module.retired && module.allowedPersonas.includes(persona))
    .map((module) => ({ id: module.id, label: module.label, icon: module.icon }));

export const resolveEntryPath = (module: ShellModule, persona: ShellPersona): string | null =>
  module.entryPaths[persona]
  ?? module.entryPaths.staff
  ?? module.entryPaths.administrator
  ?? module.entryPaths['super-administrator']
  ?? null;

export const resolveDefaultModuleId = (persona: ShellPersona): ModuleId => {
  return 'admissions';
};

export const sortModulesAlpha = (modules: ShellModule[]): ShellModule[] =>
  [...modules].sort((a, b) => a.label.localeCompare(b.label));

export const resolveModuleFromPath = (pathname: string): ShellModule | null => {
  const matchesRoot = (path: string, root: string) => path === root || path.startsWith(`${root}/`);
  const candidates = [...SHELL_MODULES, SETTINGS_MODULE]
    .filter((module) => matchesRoot(pathname, module.routeRoot))
    .sort((a, b) => b.routeRoot.length - a.routeRoot.length);
  return candidates[0] ?? null;
};
